import { Component, computed, input, output, signal, viewChild } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe, UpperCasePipe, LowerCasePipe } from '@angular/common';

import { DynamicPipe } from '@shared/pipes/dynamic.pipe';

import { Table, TableModule } from 'primeng/table';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { IColumn } from '@shared/interceptors/column.interface';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ITableChangeEvent } from '@app/shared/interfaces/table-change-event.interface';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-table-component',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    DynamicPipe,
    TableModule,
    IconField,
    InputIcon,
    InputText,
  ],
  providers: [DatePipe, DecimalPipe, UpperCasePipe, LowerCasePipe],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent<T> {
  dt = viewChild<Table>('dt');
  private destroy$ = new Subject<void>();

  data = input<T[]>([]);
  cols = input<IColumn[]>([]);
  loading = input(true);
  searchable = input(false);
  searchInputPlaceholder = input('Search');
  scrollHeight = input<string | undefined>(undefined);
  evenRowColor = input('#FFFFFF');
  oddRowColor = input('#FFFFFF');
  rowsPerPage = input(10);
  totalRecords = input(0);
  pagination = input(true);

  onChangePage = output<any>();
  searchFilter = output<string | null>();

  currentPage = signal<number>(1);
  searchControl = new FormControl('');

  // --- Computed Operations ---
  isPreviewButtonDisabled = computed(() => {
    return this.currentPage() === 1;
  });

  isNextButtonDisabled = computed(() => {
    return this.currentPage() === this.totalPages;
  });

  getGlobalFilterColumns = computed(() => {
    return this.cols()
      .filter((col) => col.searchable === true)
      .map((col) => col.key);
  });

  get totalPages(): number {
    return Math.ceil(this.totalRecords() / this.rowsPerPage());
  }

  ngOnInit(): void {
    this.searchController();
  }

  // --- Search Operations ---
  searchController() {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.searchFilter.emit(value);
      });
  }

  searchOnGivenColumn(value: string, field: string) {
    this.dt()?.filter(value, field, 'contains');
  }

  clearSearch() {
    this.searchControl.setValue('');
    this.dt()?.clear();
  }

  // --- Pagination Operations ---
  nextPage() {
    if (this.currentPage() < this.totalPages) {
      this.currentPage.update((prev) => prev + 1);

      const tableChangeEvent: ITableChangeEvent = {
        page: this.currentPage() - 1,
        size: this.rowsPerPage(),
      };

      this.onChangePage.emit(tableChangeEvent);
    }
  }

  previousPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update((prev) => prev - 1);

      const tableChangeEvent: ITableChangeEvent = {
        page: this.currentPage() - 1,
        size: this.rowsPerPage(),
      };

      this.onChangePage.emit(tableChangeEvent);
    }
  }
}
